/**
 * Сводка прогонов CI для экрана мониторинга.
 *
 * Экран отвечает на вопрос «в каком состоянии сейчас каждая ветка каждого
 * сервиса», поэтому основа — GetPipelineSummary: по одному, самому свежему,
 * прогону на пару «сервис и ветка». Сотня прогонов одной ветки за день — это
 * одна строка, последняя.
 *
 * Сводка привязана к проекту трекера: иерархия задач, по которой считается
 * эпик строки, живёт внутри проекта. Поэтому экран всегда смотрит на один
 * выбранный проект, а не на все сразу.
 */
import { gitlabApi } from '@/api'

import type { TrackerPipelineSummaryRow, V1Pipeline } from '@/api/clients/tracker-gitlab'

/** Прогон в том виде, в каком его показывает строка экрана */
export interface PipelineRun {
  id: string
  pipelineId: string
  projectPath: string
  ref: string
  sha: string
  status: string
  url: string
  durationSeconds: number
  createdAt: string
}

/** Строка сводки: прогон вместе с задачей и её эпиком */
export interface SummaryRow {
  run: PipelineRun
  issueId: string
  issueKey: string
  issueTitle: string
  epicId: string
  epicKey: string
  epicTitle: string
}

/** Группа строк: эпик или сервис — смотря как экран сейчас сгруппирован */
export interface PipelineGroup {
  key: string
  title: string
  /** Ключ задачи-эпика; у группировки по сервисам его нет */
  epicKey: string
  rows: SummaryRow[]
  counts: Record<string, number>
  /** Есть ли в группе упавшее — шапка такой группы красная */
  hasFailed: boolean
}

export type GroupBy = 'epic' | 'service'

/** Статусы, после которых прогон уже не изменится */
const FINAL_STATUSES = ['success', 'failed', 'canceled', 'skipped', 'manual']

const runOf = (pipeline: V1Pipeline): PipelineRun => ({
  id: pipeline.id ?? '',
  pipelineId: pipeline.pipelineId ?? '',
  projectPath: pipeline.gitlabProjectPath ?? '',
  ref: pipeline.ref ?? '',
  sha: pipeline.sha ?? '',
  status: pipeline.status ?? '',
  url: pipeline.webUrl ?? '',
  durationSeconds: Number(pipeline.durationSeconds ?? 0),
  createdAt: pipeline.createdAt ?? '',
})

const rowOf = (row: TrackerPipelineSummaryRow): SummaryRow => ({
  run: runOf(row.pipeline ?? {}),
  issueId: row.issueId ?? '',
  issueKey: row.issueKey ?? '',
  issueTitle: row.issueTitle ?? '',
  epicId: row.rootId ?? '',
  epicKey: row.rootKey ?? '',
  epicTitle: row.rootTitle ?? '',
})

export const getSummary = async (projectId: string, epicId?: string): Promise<SummaryRow[]> => {
  const { data } = await gitlabApi.gitLabServiceGetPipelineSummary(projectId, epicId)

  return (data.rows ?? []).map(rowOf)
}

/**
 * Последние прогоны — для полосы показателей: сводка для них не годится, она
 * схлопывает историю ветки в одну строку, а показатели считают именно прогоны.
 */
export const listRecent = async (perPage = 200): Promise<PipelineRun[]> => {
  const { data } = await gitlabApi.gitLabServiceListPipelines(
    undefined,
    undefined,
    undefined,
    true,
    '1',
    String(perPage),
  )

  return (data.pipelines ?? []).map(runOf)
}

/** Идёт ли прогон прямо сейчас */
export const isLive = (run: PipelineRun): boolean => !FINAL_STATUSES.includes(run.status)

/**
 * Группировка строк — по эпику или по сервису.
 *
 * Группы упорядочены по свежести: наверх ту, где только что что-то бежало.
 * Внутри группы тот же порядок — иначе строка, ради которой экран открывали,
 * оказывается внизу списка.
 */
export const groupRows = (rows: SummaryRow[], by: GroupBy): PipelineGroup[] => {
  const groups = new Map<string, PipelineGroup>()

  for (const row of rows) {
    const key = by === 'epic' ? row.epicId || row.issueId : row.run.projectPath
    const group = groups.get(key) ?? {
      key,
      title: by === 'epic' ? row.epicTitle || row.issueTitle : row.run.projectPath,
      epicKey: by === 'epic' ? row.epicKey : '',
      rows: [],
      counts: {},
      hasFailed: false,
    }

    group.rows.push(row)
    group.counts[row.run.status] = (group.counts[row.run.status] ?? 0) + 1
    group.hasFailed ||= row.run.status === 'failed'

    groups.set(key, group)
  }

  const freshest = (group: PipelineGroup) =>
    Math.max(...group.rows.map((row) => new Date(row.run.createdAt).getTime() || 0))

  for (const group of groups.values()) {
    group.rows.sort(
      (a, b) => new Date(b.run.createdAt).getTime() - new Date(a.run.createdAt).getTime(),
    )
  }

  return [...groups.values()].sort((a, b) => freshest(b) - freshest(a))
}

/** Полоса показателей экрана — счёт по прогонам за сутки и позавчерашним. */
export interface Kpi {
  successful: number
  finished: number
  failed: number
  failedServices: string[]
  running: number
  runningServices: string[]
  /** Средняя длительность завершённого прогона за сутки, в секундах */
  averageSeconds: number
  /** Насколько средняя изменилась к предыдущим суткам, в секундах */
  averageDeltaSeconds: number
}

const DAY = 86_400_000

const average = (runs: PipelineRun[]) =>
  runs.length ? Math.round(runs.reduce((sum, run) => sum + run.durationSeconds, 0) / runs.length) : 0

export const toKpi = (runs: PipelineRun[], now = new Date()): Kpi => {
  const since = now.getTime() - DAY
  const dayBefore = since - DAY
  const at = (run: PipelineRun) => new Date(run.createdAt).getTime() || 0

  const today = runs.filter((run) => at(run) >= since)
  const yesterday = runs.filter((run) => at(run) >= dayBefore && at(run) < since)
  // Средняя считается по завершённым: у бегущего прогона длительности ещё нет,
  // и он утянул бы её к нулю.
  const finishedToday = today.filter((run) => !isLive(run) && run.durationSeconds > 0)
  const failed = today.filter((run) => run.status === 'failed')
  const running = runs.filter(isLive)

  return {
    successful: today.filter((run) => run.status === 'success').length,
    finished: today.filter((run) => !isLive(run)).length,
    failed: failed.length,
    failedServices: [...new Set(failed.map((run) => run.projectPath))],
    running: running.length,
    runningServices: [...new Set(running.map((run) => run.projectPath))],
    averageSeconds: average(finishedToday),
    averageDeltaSeconds:
      average(finishedToday) -
      average(yesterday.filter((run) => !isLive(run) && run.durationSeconds > 0)),
  }
}

/** Длительность прогона словами: «4m 12s», как в макете. */
export const formatDuration = (seconds: number): string => {
  if (seconds <= 0) return ''

  const minutes = Math.floor(seconds / 60)

  return minutes ? `${minutes}m ${String(seconds % 60).padStart(2, '0')}s` : `${seconds}s`
}
